/**
 * @file normalizeTranslatedReportText.ts
 * @description Repairs escape sequences that survive as literal two-character
 * text in translated scout report bodies (SE-6969).
 * @module reports
 */

/** Literal backslash-r followed by literal backslash-n (Windows-style pair) */
const LITERAL_CRLF = /\\r\\n/g;

/** Literal backslash followed by the letter n */
const LITERAL_LF = /\\n/g;

/** Literal backslash followed by the letter t */
const LITERAL_TAB = /\\t/g;

/**
 * Converts literal escape sequences in translated report text into the real
 * characters they stand for.
 *
 * The translation pipeline (Bedrock, added in SE-6514) returns `qnaText` whose
 * newlines have been escaped twice, so what reaches the client is the
 * two-character text `\` + `n` rather than a newline. Markdown then renders it
 * verbatim, which is why a translated report shows runs like
 * `**Escalação Inicial**\nfalse` on a single line instead of separate
 * paragraphs.
 *
 * This is repaired on the client rather than in the backend because the backend
 * caches each translation per-locale in the `translations` JSONB column and
 * never invalidates it: a backend-side fix would only correct reports
 * translated after the deploy, leaving every already-cached translation
 * corrupted forever. Normalizing on read repairs old and new alike.
 *
 * A `JSON.parse` round-trip is deliberately not used: the incoming text is not
 * a JSON string literal, so real newlines and unescaped quotes — both common
 * in report bodies — would make it throw. A targeted replace degrades
 * gracefully instead.
 *
 * The function is idempotent: clean text (already containing real newlines)
 * passes through unchanged, so running it twice equals running it once. Only
 * the translated path calls it; the original/untranslated report text is never
 * passed through it.
 *
 * @param text - Translated report body as returned by the translation mutation
 * @returns The same text with literal escape sequences turned into real
 * characters
 * @example
 * normalizeTranslatedReportText("Line one\\nLine two")
 * // => "Line one\nLine two"
 */
export function normalizeTranslatedReportText(text: string): string {
  return text
    .replace(LITERAL_CRLF, "\n")
    .replace(LITERAL_LF, "\n")
    .replace(LITERAL_TAB, "\t");
}
